<template>
  <v-row>
    <v-col cols="12">
      <v-card class="tm-card" flat>
        <div class="tm-header pl-base-bg">
          <div class="tm-header-left">
            <div class="tm-header-icon">
              <v-icon color="#fff" size="22">mdi-account-group-outline</v-icon>
            </div>
            <div>
              <div class="tm-title">Team Members</div>
              <div class="tm-subtitle">
                Invite people to help manage your business's books
                <span v-if="limit != null">&middot; {{ used }} of {{ limit }} used</span>
              </div>
            </div>
          </div>

          <v-tooltip top :disabled="!atLimit">
            <template #activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                <v-btn class="tm-invite-btn" small :disabled="atLimit" @click="inviteDialog = true">
                  <v-icon left size="16">mdi-account-plus-outline</v-icon>
                  Invite Member
                </v-btn>
              </span>
            </template>
            <span>You've reached the limit of {{ limit }} team members. Remove someone or revoke a pending invite first.</span>
          </v-tooltip>
        </div>

        <div class="pa-6">
          <div class="tm-section-label">Active Members</div>

          <div v-if="!members.length" class="tm-empty">
            No team members yet — invite someone to get started.
          </div>

          <div v-for="member in members" :key="member._id" class="tm-row">
            <div class="tm-row-main">
              <div class="tm-row-name">{{ member.name }}</div>
              <div class="tm-row-email">{{ member.email }}</div>
            </div>

            <div class="tm-row-actions">
              <v-chip v-if="!member.isActive" color="grey" dark small class="mr-2">Deactivated</v-chip>

              <v-select
                :value="member.teamRole"
                :items="roleOptions"
                dense
                outlined
                hide-details
                class="tm-role-select"
                @change="(val) => changeRole(member, val)"
              />

              <v-btn
                v-if="member.isActive"
                small
                text
                color="warning"
                @click="confirmDeactivate(member)"
              >
                Deactivate
              </v-btn>

              <v-btn small text color="error" @click="confirmRemove(member)">
                Remove
              </v-btn>
            </div>
          </div>

          <v-divider class="my-6" />

          <div class="tm-section-label">Pending Invitations</div>

          <div v-if="!pendingInvitations.length" class="tm-empty">
            No pending invitations.
          </div>

          <div v-for="invite in pendingInvitations" :key="invite._id" class="tm-row">
            <div class="tm-row-main">
              <div class="tm-row-name">{{ invite.name }}</div>
              <div class="tm-row-email">{{ invite.email }} &middot; {{ roleLabel(invite.teamRole) }}</div>
            </div>

            <div class="tm-row-actions">
              <v-btn small text color="error" @click="revoke(invite)">
                Revoke
              </v-btn>
            </div>
          </div>
        </div>
      </v-card>
    </v-col>

    <GlobalDialog
      v-if="inviteDialog"
      v-model="inviteDialog"
      title="Invite Team Member"
      icon="mdi-account-plus-outline"
      :width="480"
      :buttons="inviteButtons"
    >
      <v-form ref="inviteForm" v-model="inviteValid">
        <v-text-field
          v-model="inviteForm.name"
          label="Full Name"
          outlined
          dense
          :rules="[rules.required]"
          prepend-inner-icon="mdi-account"
        />
        <v-text-field
          v-model="inviteForm.email"
          label="Email"
          outlined
          dense
          type="email"
          :rules="[rules.required, rules.email]"
          prepend-inner-icon="mdi-email-outline"
        />
        <v-select
          v-model="inviteForm.teamRole"
          :items="roleOptions"
          label="Access Level"
          outlined
          dense
          prepend-inner-icon="mdi-shield-account-outline"
        />
        <div class="tm-role-hint">
          {{ inviteForm.teamRole === "VIEW_ONLY"
            ? "Can view everything but cannot create, edit, or delete anything."
            : "Can create, edit, and delete like you can — except Business Profile, Bank Account, and Terms & Conditions, which stay owner-only."
          }}
        </div>
      </v-form>
    </GlobalDialog>

    <GlobalDialog
      v-if="confirmDialog.show"
      v-model="confirmDialog.show"
      :title="confirmDialog.title"
      icon="mdi-alert-outline"
      :width="420"
      :buttons="confirmButtons"
    >
      <p class="mb-0">{{ confirmDialog.message }}</p>
    </GlobalDialog>
  </v-row>
</template>

<script>
import GlobalDialog from "./common/global-dialog.vue";

export default {
  name: "TeamMembers",
  components: { GlobalDialog },

  data() {
    return {
      loading: false,
      sending: false,

      inviteDialog: false,
      inviteValid: false,
      inviteForm: {
        name: "",
        email: "",
        teamRole: "FULL_ACCESS",
      },

      roleOptions: [
        { text: "Full Access", value: "FULL_ACCESS" },
        { text: "View Only", value: "VIEW_ONLY" },
      ],

      confirmDialog: {
        show: false,
        title: "",
        message: "",
        confirmText: "Confirm",
        loading: false,
        pendingAction: null,
        member: null,
      },

      rules: {
        required: (v) => !!v || "Required",
        email: (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || "Enter a valid email address",
      },
    };
  },

  created() {
    this.fetchAll();
  },

  computed: {
    members() {
      return this.$store.state.team.members;
    },

    pendingInvitations() {
      return (this.$store.state.team.invitations || []).filter((i) => i.status === "PENDING");
    },

    limit() {
      return this.$store.state.team.limit;
    },

    used() {
      return this.$store.state.team.used;
    },

    atLimit() {
      return this.limit != null && this.used != null && this.used >= this.limit;
    },

    inviteButtons() {
      return [
        {
          text: "Cancel",
          outlined: true,
          color: "#0a5a90",
          action: () => (this.inviteDialog = false),
        },
        {
          text: "Send Invite",
          loading: this.sending,
          disabled: !this.inviteValid || this.atLimit,
          action: this.sendInvite,
        },
      ];
    },

    confirmButtons() {
      return [
        {
          text: "Cancel",
          outlined: true,
          color: "#0a5a90",
          action: () => (this.confirmDialog.show = false),
        },
        {
          text: this.confirmDialog.confirmText,
          color: "error",
          loading: this.confirmDialog.loading,
          action: this.runConfirmedAction,
        },
      ];
    },
  },

  methods: {
    async fetchAll() {
      this.loading = true;
      try {
        await Promise.all([
          this.$store.dispatch("team/getMembers"),
          this.$store.dispatch("team/getInvitations"),
          this.$store.dispatch("team/getLimit"),
        ]);
      } catch (err) {
        this.$toast?.error("Failed to load team");
      } finally {
        this.loading = false;
      }
    },

    roleLabel(role) {
      return role === "VIEW_ONLY" ? "View Only" : "Full Access";
    },

    async sendInvite() {
      if (!this.$refs.inviteForm.validate()) return;

      this.sending = true;
      try {
        await this.$store.dispatch("team/sendInvite", { ...this.inviteForm });
        this.$toast?.success("Invite sent");
        this.inviteDialog = false;
        this.inviteForm = { name: "", email: "", teamRole: "FULL_ACCESS" };
      } catch (err) {
        this.$toast?.error(err?.response?.data?.message || "Failed to send invite");
      } finally {
        this.sending = false;
      }
    },

    async changeRole(member, teamRole) {
      try {
        await this.$store.dispatch("team/updateMemberRole", { id: member._id, teamRole });
        this.$toast?.success("Role updated");
      } catch (err) {
        this.$toast?.error(err?.response?.data?.message || "Failed to update role");
      }
    },

    async revoke(invite) {
      try {
        await this.$store.dispatch("team/revokeInvite", invite._id);
        this.$toast?.success("Invitation revoked");
      } catch (err) {
        this.$toast?.error(err?.response?.data?.message || "Failed to revoke invitation");
      }
    },

    confirmDeactivate(member) {
      this.confirmDialog = {
        show: true,
        title: "Deactivate Team Member",
        message: `${member.name} will no longer be able to log in. You can remove them entirely instead if this is permanent.`,
        confirmText: "Deactivate",
        loading: false,
        pendingAction: "deactivate",
        member,
      };
    },

    confirmRemove(member) {
      this.confirmDialog = {
        show: true,
        title: "Remove Team Member",
        message: `This permanently deletes ${member.name}'s login. This cannot be undone. Anything they created will stay in your books, just no longer linked to an active account.`,
        confirmText: "Remove",
        loading: false,
        pendingAction: "remove",
        member,
      };
    },

    async runConfirmedAction() {
      const { pendingAction, member } = this.confirmDialog;
      this.confirmDialog.loading = true;

      try {
        if (pendingAction === "deactivate") {
          await this.$store.dispatch("team/deactivateMember", member._id);
          this.$toast?.success("Team member deactivated");
        } else if (pendingAction === "remove") {
          await this.$store.dispatch("team/removeMember", member._id);
          this.$toast?.success("Team member removed");
        }
        this.confirmDialog.show = false;
      } catch (err) {
        this.$toast?.error(err?.response?.data?.message || "Action failed");
      } finally {
        this.confirmDialog.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.tm-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

.tm-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 14px 20px;
}

.tm-header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.tm-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.tm-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.tm-subtitle {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.tm-invite-btn {
  background: rgba(255, 255, 255, 0.16) !important;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #fff !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.tm-section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 12px;
}

.tm-empty {
  color: #94a3b8;
  font-size: 13.5px;
  padding: 12px 0 20px;
}

.tm-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 12px 16px;
  margin-bottom: 10px;
}

.tm-row-name {
  font-weight: 700;
  font-size: 14px;
  color: #0f172a;
}

.tm-row-email {
  font-size: 12.5px;
  color: #64748b;
  margin-top: 2px;
}

.tm-row-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.tm-role-select {
  max-width: 160px;
}

.tm-role-hint {
  font-size: 12.5px;
  color: #64748b;
  margin-top: -4px;
}
</style>
